import { Calendar, MapPin, Clock, Star, X } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './EventList.module.css';

/**
 * EventList - List of events for Upcoming or History tabs
 * 
 * Features:
 * - Event card with date, title, location
 * - Cancel button for upcoming events
 * - Rating display/action for past events
 * - Empty state
 */
export function EventList({ events, type, onEventClick, onCancelEvent, onRateEvent }) {
  if (events.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size="base" color="muted" align="center">
          No {type} events yet
        </Text>
      </div>
    );
  }

  return (
    <Stack direction="column" gap={3}>
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          type={type}
          onClick={() => onEventClick?.(event.id)}
          onCancel={() => onCancelEvent?.(event.id)}
          onRate={() => onRateEvent?.(event.id)}
        />
      ))}
    </Stack>
  );
}

/**
 * EventCard - Individual event item
 */
function EventCard({ event, type, onClick, onCancel, onRate }) {
  const isUpcoming = type === 'upcoming';

  return (
    <div className={styles.card} onClick={onClick}>
      {/* Date Column */}
      <div className={styles.dateColumn}>
        <Calendar size={18} className={styles.dateIcon} />
        <Text size="sm" weight="medium" color="primary" className={styles.dateText}>
          {event.date}
        </Text>
        {isUpcoming && event.time && (
          <div className={styles.time}>
            <Clock size={12} />
            <Text size="xs" color="muted">{event.time}</Text>
          </div>
        )}
      </div>

      {/* Event Info */}
      <div className={styles.info}>
        <Text size="base" weight="medium" color="primary" className={styles.title}>
          {event.title}
        </Text>
        <div className={styles.location}>
          <MapPin size={14} />
          <Text size="sm" color="muted">{event.location}</Text>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        {isUpcoming ? (
          <button
            className={styles.cancelButton}
            onClick={(e) => {
              e.stopPropagation();
              onCancel?.();
            }}
            aria-label="Cancel event"
          >
            <X size={18} />
          </button>
        ) : (
          <div className={styles.rating}>
            {event.rating ? (
              <div className={styles.stars}>
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < event.rating ? styles.starFilled : styles.starEmpty}
                    fill={i < event.rating ? 'currentColor' : 'none'}
                  />
                ))}
              </div>
            ) : (
              <button
                className={styles.rateButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onRate?.();
                }}
              >
                <Star size={16} />
                <Text size="xs">Rate</Text>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EventList;
